import type { RentalscapeConfig } from '@/api/ariadne/__generated_types__/graphql';
import { COMPLAINT_FIELDS } from '@/complaints/complaints-constants';
import {
  FIRST_ITEM_INDEX,
  INIT_NOTE_ADD_STATUS,
  IN_FLIGHT_FIELDS_KEY,
} from '@/constants';
import * as listActions from '@/list/list-actions';
import { paginatedDataReturnedReducerFactory } from '@/utils/paginated-fetch';
import { List, Map, Record, Set, fromJS, isList } from 'immutable';
import { Reducer } from 'redux';
import { handleActions } from 'redux-actions';
import { License } from '../license/license-types';
import * as dataActions from './data-actions';
import { UPDATE_COMPLAINT_STATUS } from './data-actions';
import { ListingDataRec } from './data-records';

const stateRecord = Record({
  listingsData: Map(),
  propertyList: null,
  kpiData: null,
  hostList: null,
  ownerList: null,
  stats: null,
  rentalscapeConfig: null as RentalscapeConfig | null,
  translations: {},
  hoa: null,
  propertyData: null,
  propertyComplaintsData: null,
  activityData: null,
  licensesData: null as List<License> | string | null,
  totPaymentsData: null,
  complaintsData: null,
  complaintsByProperty: Map(),
  observedViolationTypes: Set(),
  observedPlatforms: Set(),
  noteAddStatus: INIT_NOTE_ADD_STATUS,
});

export const initState = stateRecord();
export type DataState = typeof initState;

function stringOrImmutable<T>(data: string | T[]): string | List<T> {
  if (typeof data === 'string') return data;
  return List(data);
}

/**
 * Sets inFlight fields on one of the licenses used in the license tab in property history
 * to help the UI determine which action is being currently processed by the API.
 * This is helpful in deciding which button in the license switcher displays the loading
 * indicator, and could be useful for other, similar, UX decisions.
 * @param state Store state snapshot
 * @param index Index of the license in the array of licenses for the current property
 * @param inFlight Whether or not we're currently waiting on an API call to update the license
 * @param action Redux action instance that is responsible for the update.
 * This can be used to distinguish waiting for license status updates vs
 * waiting for changed to be saved after using the Edit button in the license switcher
 * @returns {*} A new store state snapshot with inFlight flags set to the requested values.
 */
function setLicenseInFlight(state: DataState, index, inFlight, action) {
  const keyPath = ['licensesData', index];

  if (!inFlight) {
    return state.mergeIn(keyPath, {
      inFlight: false,
      inFlightActionType: null,
    });
  }

  return state.mergeIn(keyPath, {
    inFlight: true,
    inFlightActionType: action.type,
  });
}

export const dataReducer: Reducer<DataState> = handleActions(
  {
    [dataActions.SERVER_RETURNED_PROPERTY_LIST]:
      paginatedDataReturnedReducerFactory(['propertyList']),

    [dataActions.SET_COMPLAINTS_BY_PROPERTY]: (state, action) => {
      const { data } = action.payload;
      return state.set('complaintsByProperty', data);
    },

    [dataActions.SET_ALL_OBSERVED_VIOLATION_TYPES]: (state, action) => {
      const { violationTypes } = action.payload;

      return state.set('observedViolationTypes', violationTypes);
    },

    [dataActions.SET_ALL_OBSERVED_PLATFORMS]: (state, action) => {
      const { platforms } = action.payload;

      return state.set('observedPlatforms', platforms);
    },

    [dataActions.SERVER_RETURNED_KPI_DATA]: (state, action) => {
      const { kpiData } = action.payload;
      return state.set('kpiData', kpiData);
    },
    [dataActions.SERVER_RETURNED_HOSTS_DATA]: (state, action) => {
      const { hostList } = action.payload;
      return state.set('hostList', hostList);
    },

    [dataActions.SERVER_RETURNED_OWNERS_DATA]: (state, action) => {
      const { ownerList } = action.payload;
      return state.set('ownerList', ownerList);
    },

    [dataActions.SERVER_RETURNED_STATS_DATA]: (state, action) => {
      const { stats } = action.payload;
      return state.set('stats', stats);
    },

    [dataActions.SERVER_RETURNED_RENTALSCAPE_CONFIG_DATA]: (state, action) => {
      const { rentalscapeConfig } = action.payload;
      return state.set('rentalscapeConfig', rentalscapeConfig);
    },

    [dataActions.SERVER_RETURNED_TRANSLATIONS_DATA]: (state, action) => {
      const { translations } = action.payload;
      return state.set('translations', translations);
    },

    [listActions.SET_LISTING_DATA_INFLIGHT]: (state, action) => {
      const { listingType } = action.payload;

      const rec =
        state.getIn(['listingsData', listingType]) || new ListingDataRec();
      return state.setIn(['listingsData', listingType], rec.setInFlight());
    },

    [listActions.SERVER_RETURNED_LISTING_DATA]: (state, action) => {
      const { listingType, listingData } = action.payload;
      const rec = state.getIn(['listingsData', listingType]);
      if (!rec) {
        throw new Error(
          `Listing data record not initialized for ${listingType}`
        );
      }
      return state.setIn(
        ['listingsData', listingType],
        rec.setData(listingData)
      );
    },

    [dataActions.SET_HOA_DATA_ACTION]: (state, action) => {
      const { hoa } = action.payload;
      return state.set('hoa', hoa);
    },

    [dataActions.SERVER_RETURNED_PROPERTY_DATA]: (state, action) => {
      const { data } = action.payload;
      return state.set('propertyData', data);
    },

    [dataActions.SERVER_RETURNED_PROPERTY_COMPLAINTS_DATA]: (state, action) => {
      const { data } = action.payload;
      return state.set('propertyComplaintsData', data);
    },

    [dataActions.UPDATE_SERVER_RETURNED_PROPERTY_COMPLAINTS_DATA]: (
      state,
      action
    ) => {
      const { complaintId, updatePayload } = action.payload;

      const updatedData = state.get('propertyComplaintsData').map((item) => {
        if (item.complaintId === complaintId) {
          return {
            ...item,
            ...updatePayload,
          };
        }

        return item;
      });

      return state.set('propertyComplaintsData', updatedData);
    },

    [dataActions.SERVER_RETURNED_ACTIVITY_DATA]: (state, action) => {
      const { data } = action.payload;
      const stringOrImmutableActivityData = stringOrImmutable(data);
      return state.set('activityData', stringOrImmutableActivityData);
    },

    [dataActions.SERVER_RETURNED_LICENSES_DATA]: (state: DataState, action) => {
      const { data } = action.payload;

      const stringOrImmutableLicensesData = stringOrImmutable(
        data as string | License[]
      );

      const dataIsListType = isList(stringOrImmutableLicensesData);
      const licenseData = dataIsListType
        ? stringOrImmutableLicensesData.sort(
            (a, b) => a.licenseId - b.licenseId
          )
        : stringOrImmutableLicensesData;

      return state.set('licensesData', licenseData);
    },

    [dataActions.SERVER_RETURNED_TOT_PAYMENTS_DATA]: (
      state: DataState,
      action
    ) => {
      const { data } = action.payload;

      const stringOrImmutableLicensesData = stringOrImmutable<License>(data);

      const dataIsListType = isList(stringOrImmutableLicensesData);
      const licenseData = dataIsListType
        ? stringOrImmutableLicensesData.sort(
            (a, b) => a.licenseId - b.licenseId
          )
        : stringOrImmutableLicensesData;

      return state.set('totPaymentsData', licenseData);
    },

    [dataActions.UPDATE_NOTE]: (state, action) => {
      // new value is the new text value/message in the note
      const { index, value } = action.payload;

      return state.setIn(['activityData', index, 'value'], value);
    },

    [dataActions.ADD_NOTE]: (state, action) => {
      // new value is the new text value/message in the note
      // currNoteTimestamp is used to identify the note when it has a Temp Id
      const { message, currNoteTimestamp, author, event } = action.payload;

      const activityDataImm = state.activityData;
      const newItem = {
        type: 'note',
        // adding temp id to keep consistency. The entire object should be overwritten on POST success
        value: {
          message,
          id: `tempId-${currNoteTimestamp}`,
          currNoteTimestamp,
          author,
          event,
        },
      };
      const newActivityData = activityDataImm.insert(FIRST_ITEM_INDEX, newItem);
      return state.set('activityData', newActivityData);
    },

    /**
     * Update a note that was added and had no id
     *
     * @param {*} state
     * @param {*} action
     * @returns
     */
    [dataActions.UPDATE_TEMP_NOTE]: (state, action) => {
      const { currNoteTimestamp, note } = action.payload;

      const index = state.activityData.findIndex(
        (item) => item?.value?.currNoteTimestamp === currNoteTimestamp
      );

      return state.setIn(['activityData', index, 'value'], note);
    },

    [dataActions.SERVER_FAILED_TO_ADD_NOTE]: (state, action) => {
      // new value is the new text value/message in the note
      const { currNoteTimestamp } = action.payload;

      const index = state.activityData.findIndex(
        (item) => item?.value?.currNoteTimestamp === currNoteTimestamp
      );

      return state.removeIn(['activityData', index]);
    },

    [dataActions.UPDATE_LICENSE_STATUS]: (state: DataState, action) => {
      const { status, index, inFlight } = action.payload;
      const newState = state.setIn(['licensesData', index, 'status'], status);
      return setLicenseInFlight(newState, index, inFlight, action);
    },

    [dataActions.UPDATE_LICENSE_RENEWAL_STATUS]: (state: DataState, action) => {
      const { renewalStatus, index, inFlight } = action.payload;
      return state
        .setIn(
          ['licensesData', index, 'renewals', FIRST_ITEM_INDEX, 'status'],
          renewalStatus
        )
        .setIn(
          ['licensesData', index, 'renewals', FIRST_ITEM_INDEX, 'inFlight'],
          inFlight
        );
    },

    [dataActions.SERVER_RETURNED_UPDATED_LICENSE]: (
      state: DataState,
      action
    ) => {
      const { license, index } = action.payload;
      return state.setIn(['licensesData', index], license);
    },

    [dataActions.SET_NOTE_ADD_STATUS]: (state, action) => {
      const { inFlight, addedNoteId } = action.payload;

      return state.set('noteAddStatus', { inFlight, addedNoteId });
    },

    [dataActions.SERVER_SENT_LICENSE_TO_HOLDER]: (state: DataState, action) => {
      const { index, license } = action.payload;
      return state.setIn(['licensesData', index], license);
    },

    [dataActions.clearPropertyDetailsData.type]: (state: DataState) =>
      state
        .set('propertyData', null)
        .set('licensesData', null)
        .set('activityData', null),

    [dataActions.SERVER_RETURNED_COMPLAINTS]:
      paginatedDataReturnedReducerFactory(['complaintsData']),

    [UPDATE_COMPLAINT_STATUS]: (state, action) => {
      const { complaintRowIndex, status, inFlight } = action.payload;

      const stateWithStatus = state.setIn(
        ['complaintsData', complaintRowIndex, COMPLAINT_FIELDS.STATUS],
        status
      );

      if (!inFlight) {
        return stateWithStatus.removeIn([
          'complaintsData',
          complaintRowIndex,
          IN_FLIGHT_FIELDS_KEY,
        ]);
      }

      return stateWithStatus.setIn(
        ['complaintsData', complaintRowIndex, IN_FLIGHT_FIELDS_KEY],
        // This is not a constructor, but factory function from immutable.js.
        Set([COMPLAINT_FIELDS.STATUS])
      );
    },

    [dataActions.UPDATE_LICENSE]: (state: DataState, action) => {
      const {
        partialLicense = {},
        replaceLicense,
        index,
        inFlight,
      } = action.payload;
      const licenseKeyPath = ['licensesData', index];

      if (replaceLicense) {
        return setLicenseInFlight(
          state.setIn(licenseKeyPath, replaceLicense),
          index,
          inFlight,
          action
        );
      }

      // Some people types could be arrays and mergeDeep would effectively
      // concatenate the old and new array elements.
      // Perform a non-deep merge to replace old people with new people
      // instead of merging in that case
      const partialLicenseWithoutPeople = { ...partialLicense };
      delete partialLicenseWithoutPeople.people;

      let updatedLicense = fromJS(state.getIn(licenseKeyPath)).mergeDeep(
        partialLicenseWithoutPeople
      );

      if (partialLicense.people) {
        updatedLicense = updatedLicense.mergeIn(
          ['people'],
          partialLicense.people
        );
      }

      const newState = state.setIn(licenseKeyPath, updatedLicense.toJS());

      return setLicenseInFlight(newState, index, inFlight, action);
    },
  },
  initState
);
